/**
 * API Service for communicating with the backend
 */
const API_URL = process.env.REACT_APP_API_URL;

const apiService = {
  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise} - Response from the API
   */
  signUp: async (userData) => {
    try {
      const response = await fetch(`${API_URL}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'An error occurred during registration');
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Sign in existing user
   * @param {Object} credentials - User login credentials
   * @returns {Promise} - Response from the API
   */
  signIn: async (credentials) => {
    try {
      const response = await fetch(`${API_URL}/auth/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'An error occurred during sign in');
      }
      
      if (data.data && data.data.session) {
        localStorage.setItem('authToken', data.data.session.access_token);
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Sign out user
   * @returns {Promise} - Response from the API
   */
  signOut: async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/auth/signout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'An error occurred during sign out');
      }
      
      localStorage.removeItem('authToken');
      
      return data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get all zones
   * @returns {Promise} - Response from the API
   */
  getAllZones: async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/owner/zones`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'An error occurred while fetching zones');
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get zone by ID
   * @param {string} zoneId - ID of the zone to fetch
   * @returns {Promise} - Response from the API
   */
  getZoneById: async (zoneId) => {
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/owner/zones/${zoneId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'An error occurred while fetching the zone');
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Create a new zone
   * @param {Object} zoneData - Zone data to create
   * @returns {Promise} - Response from the API
   */
  createZone: async (zoneData) => {
    try {
      const token = localStorage.getItem('authToken');
      
      const payload = {
        name: zoneData.name,
        hourly_rate: zoneData.hourly_rate,
      };
      
      const response = await fetch(`${API_URL}/owner/zones`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'An error occurred while creating the zone');
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update an existing zone
   * @param {string} zoneId - ID of the zone to update
   * @param {Object} zoneData - Updated zone data
   * @returns {Promise} - Response from the API
   */
  updateZone: async (zoneId, zoneData) => {
    try {
      const token = localStorage.getItem('authToken');
      
      const payload = {
        name: zoneData.name,
        hourly_rate: zoneData.hourly_rate
      };
      
      const response = await fetch(`${API_URL}/owner/zones/${zoneId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'An error occurred while updating the zone');
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete a zone
   * @param {string} zoneId - ID of the zone to delete
   * @returns {Promise} - Response from the API
   */
  deleteZone: async (zoneId) => {
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/owner/zones/${zoneId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'An error occurred while deleting the zone');
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Check Stripe account status
   * @returns {Promise} - Response from the API with the Stripe account status
   */
  checkStripeAccountStatus: async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/stripe/account-status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'An error occurred while checking Stripe account status');
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get Stripe onboarding URL
   * @returns {Promise} - Response from the API with onboarding URL
   */
  getStripeOnboardingUrl: async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/stripe/onboarding-url`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'An error occurred while getting Stripe onboarding URL');
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get zone pricing information
   * @param {string} zoneId - ID of the zone to get pricing for
   * @param {number} duration - Duration in minutes (optional)
   * @returns {Promise} - Response from the API with pricing details
   */
  getZonePricing: async (zoneId, duration = null) => {
    try {
      let url = `${API_URL}/parking-pricing?zone_id=${zoneId}`;
      
      if (duration !== null) {
        url += `&duration=${duration}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'An error occurred while fetching zone pricing');
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Create a new parking session
   * @param {Object} sessionData - Parking session data
   * @returns {Promise} - Response from the API
   */
  createParkingSession: async (sessionData) => {
    try {
      const response = await fetch(`${API_URL}/parking-sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          license_plate: sessionData.licensePlate,
          email: sessionData.email,
          zone_id: sessionData.zoneId,
          duration: sessionData.duration
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'An error occurred while creating the parking session');
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Create a payment intent for a parking session
   * @param {Object} paymentData - Data required for the payment
   * @returns {Promise} - Response from the API
   */
  createPaymentIntent: async (paymentData) => {
    try {
      const response = await fetch(`${API_URL}/payments/create-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: paymentData.amount,
          email: paymentData.email,
          description: `Parking payment for ${paymentData.licensePlate} in zone ${paymentData.zoneId}`,
          zone_code: paymentData.zone_code,
          license_plate: paymentData.licensePlate,
          duration: paymentData.duration,
          parking_fee: paymentData.parkingFee,
          processing_fee: paymentData.processingFee
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'An error occurred while creating payment intent');
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Confirm a payment and create a parking session
   * @param {Object} confirmData - Data required to confirm the payment
   * @returns {Promise} - Response from the API
   */
  confirmPaymentAndCreateSession: async (confirmData) => {
    try {
      const response = await fetch(`${API_URL}/payment/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(confirmData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'An error occurred while confirming payment');
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Check the status of a payment
   * @param {string} paymentIntentId - The ID of the payment intent to check
   * @returns {Promise} - Response from the API with payment status
   */
  checkPaymentStatus: async (paymentIntentId) => {
    try {
      const response = await fetch(`${API_URL}/payments/status/${paymentIntentId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'An error occurred while checking payment status');
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  }
};

export default apiService;